import React, { useState, useEffect } from 'react';
import { CogIcon, GlobeAltIcon, BellIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [dmPollingMethod, setDmPollingMethod] = useState(null);
  const [dmPollingSaving, setDmPollingSaving] = useState(false);
  const [captchaApiKey, setCaptchaApiKey] = useState('');
  const [captchaApiKeySaving, setCaptchaApiKeySaving] = useState(false);
  const [changePasswordCurrent, setChangePasswordCurrent] = useState('');
  const [changePasswordNew, setChangePasswordNew] = useState('');
  const [changePasswordConfirm, setChangePasswordConfirm] = useState('');
  const [changePasswordSaving, setChangePasswordSaving] = useState(false);
  const [settings, setSettings] = useState({
    general: {
      maxMessageAge: 7,
      defaultDailyLimit: 50,
      defaultResponseDelay: { min: 2000, max: 8000 },
      defaultWorkingHours: { start: 9, end: 22 },
      timezone: 'UTC'
    },
    notifications: {
      emailNotifications: true,
      slackWebhook: '',
      discordWebhook: '',
      telegramBotToken: '',
      telegramChatId: ''
    },
    security: {
      sessionTimeout: 24,
      requireTwoFactor: false,
      allowedIPs: []
    }
  });

  const tabs = [
    { id: 'general', name: 'General', icon: CogIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'security', name: 'Security', icon: GlobeAltIcon }
  ];

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  // Load system settings (admin only)
  useEffect(() => {
    api.get('/admin/system-settings')
      .then((res) => {
        if (res.data?.success) {
          const s = res.data.settings;
          setDmPollingMethod(s?.dmPollingMethod ?? 'inbox_initial_state');
          setCaptchaApiKey(s?.captchaApiKey ?? '');
        } else {
          setDmPollingMethod('inbox_initial_state');
        }
      })
      .catch(() => setDmPollingMethod('inbox_initial_state'));
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(prev => ({
        ...prev,
        general: {
          ...prev.general,
          ...response.data
        }
      }));
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    }
  };

  const handleSave = async (tabId) => {
    try {
      if (tabId === 'general') {
        await api.post('/settings', settings.general);
        toast.success('General settings saved successfully!');
      } else {
        // Mock save for other tabs
        toast.success(`${tabs.find(t => t.id === tabId)?.name} settings saved successfully!`);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const handleInputChange = (tabId, field, value) => {
    setSettings(prev => ({
      ...prev,
      [tabId]: {
        ...prev[tabId],
        [field]: value
      }
    }));
  };

  const handleNestedInputChange = (tabId, parentField, field, value) => {
    setSettings(prev => ({
      ...prev,
      [tabId]: {
        ...prev[tabId],
        [parentField]: {
          ...prev[tabId][parentField],
          [field]: value
        }
      }
    }));
  };

  const saveDmPollingMethod = async () => {
    if (dmPollingMethod == null) return;
    try {
      setDmPollingSaving(true);
      await api.put('/admin/system-settings', { settings: { dmPollingMethod } });
      toast.success('DM polling method saved');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to save (admin only)');
    } finally {
      setDmPollingSaving(false);
    }
  };

  const saveCaptchaApiKey = async () => {
    try {
      setCaptchaApiKeySaving(true);
      await api.put('/admin/system-settings', { settings: { captchaApiKey } });
      toast.success('Captcha API key saved');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to save (admin only)');
    } finally {
      setCaptchaApiKeySaving(false);
    }
  };

  const GeneralSettings = () => (
    <div className="space-y-6">
      {/* DM Polling Method - system setting */}
      {dmPollingMethod != null && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">DM Inbox Polling Method</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Choose how DMs are polled for automation.</p>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={dmPollingMethod}
              onChange={(e) => setDmPollingMethod(e.target.value)}
              className="form-select max-w-md"
            >
              <option value="inbox_initial_state">inbox_initial_state (current – includes 5 min full inbox scan)</option>
              <option value="getInitialXChatPage">getInitialXChatPage (new GraphQL, non-E2EE)</option>
              <option value="xchat">XCHAT (full E2EE – key unwrap + NaCl decrypt + WebSocket)</option>
            </select>
            <button
              type="button"
              onClick={saveDmPollingMethod}
              disabled={dmPollingSaving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {dmPollingSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Captcha API Key - admin only */}
      {user?.role === 'admin' && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Captcha Solver</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">CapMonster.cloud API key for automated CAPTCHA solving on X.com accounts.</p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="password"
              value={captchaApiKey}
              onChange={(e) => setCaptchaApiKey(e.target.value)}
              className="form-input max-w-md"
              placeholder="Enter your CapMonster API key"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={saveCaptchaApiKey}
              disabled={captchaApiKeySaving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {captchaApiKeySaving ? 'Saving...' : 'Save'}
            </button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Get your API key from <a href="https://capmonster.cloud" target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:text-indigo-400 underline">capmonster.cloud</a></p>
        </div>
      )}

      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Default Automation Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max Message Age (Days)
            </label>
            <input
              type="number"
              value={settings.general.maxMessageAge}
              onChange={(e) => handleInputChange('general', 'maxMessageAge', parseInt(e.target.value))}
              className="form-input"
              min="1"
              max="30"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Maximum age of messages to respond to when bot restarts (1-30 days)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Default Daily Limit
            </label>
            <input
              type="number"
              value={settings.general.defaultDailyLimit}
              onChange={(e) => handleInputChange('general', 'defaultDailyLimit', parseInt(e.target.value))}
              className="form-input"
              min="1"
              max="200"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Maximum responses per day for new accounts</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Timezone
            </label>
            <select
              value={settings.general.timezone}
              onChange={(e) => handleInputChange('general', 'timezone', e.target.value)}
              className="form-select"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
              <option value="Europe/London">London</option>
              <option value="Europe/Paris">Paris</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Response Delay (Min)
            </label>
            <input
              type="number"
              value={settings.general.defaultResponseDelay.min}
              onChange={(e) => handleNestedInputChange('general', 'defaultResponseDelay', 'min', parseInt(e.target.value))}
              className="form-input"
              min="1000"
              max="30000"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Minimum delay in milliseconds</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Response Delay (Max)
            </label>
            <input
              type="number"
              value={settings.general.defaultResponseDelay.max}
              onChange={(e) => handleNestedInputChange('general', 'defaultResponseDelay', 'max', parseInt(e.target.value))}
              className="form-input"
              min="1000"
              max="30000"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Maximum delay in milliseconds</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Working Hours Start
            </label>
            <input
              type="number"
              value={settings.general.defaultWorkingHours.start}
              onChange={(e) => handleNestedInputChange('general', 'defaultWorkingHours', 'start', parseInt(e.target.value))}
              className="form-input"
              min="0"
              max="23"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Hour of day (24-hour format)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Working Hours End
            </label>
            <input
              type="number"
              value={settings.general.defaultWorkingHours.end}
              onChange={(e) => handleNestedInputChange('general', 'defaultWorkingHours', 'end', parseInt(e.target.value))}
              className="form-input"
              min="0"
              max="23"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Hour of day (24-hour format)</p>
          </div>
        </div>
      </div>
    </div>
  );


  const NotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Notification Preferences</h3>
        
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={settings.notifications.emailNotifications}
              onChange={(e) => handleInputChange('notifications', 'emailNotifications', e.target.checked)}
              className="form-checkbox"
            />
            <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Email Notifications</label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Slack Webhook URL
            </label>
            <input
              type="url"
              value={settings.notifications.slackWebhook}
              onChange={(e) => handleInputChange('notifications', 'slackWebhook', e.target.value)}
              className="form-input"
              placeholder="https://hooks.slack.com/services/..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Discord Webhook URL
            </label>
            <input
              type="url"
              value={settings.notifications.discordWebhook}
              onChange={(e) => handleInputChange('notifications', 'discordWebhook', e.target.value)}
              className="form-input"
              placeholder="https://discord.com/api/webhooks/..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Telegram Bot Token
              </label>
              <input
                type="text"
                value={settings.notifications.telegramBotToken}
                onChange={(e) => handleInputChange('notifications', 'telegramBotToken', e.target.value)}
                className="form-input"
                placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Telegram Chat ID
              </label>
              <input
                type="text"
                value={settings.notifications.telegramChatId}
                onChange={(e) => handleInputChange('notifications', 'telegramChatId', e.target.value)}
                className="form-input"
                placeholder="-1001234567890"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralSettings />;
      case 'notifications':
        return <NotificationSettings />;
      case 'security':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Security Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Session Timeout (hours)
                  </label>
                  <input
                    type="number"
                    value={settings.security.sessionTimeout}
                    onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
                    className="form-input"
                    min="1"
                    max="168"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">How long to keep users logged in</p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.security.requireTwoFactor}
                    onChange={(e) => handleInputChange('security', 'requireTwoFactor', e.target.checked)}
                    className="form-checkbox"
                  />
                  <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Require Two-Factor Authentication</label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Allowed IP Addresses
                  </label>
                  <textarea
                    value={settings.security.allowedIPs.join('\n')}
                    onChange={(e) => handleInputChange('security', 'allowedIPs', e.target.value.split('\n').filter(ip => ip.trim()))}
                    className="form-textarea"
                    rows="4"
                    placeholder="192.168.1.1&#10;10.0.0.1&#10;..."
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">One IP address per line. Leave empty to allow all IPs.</p>
                </div>
              </div>
            </div>
            {user?.role === 'admin' && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                  <LockClosedIcon className="h-5 w-5" />
                  Change admin password
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Update your account password. You will need to log in again after changing it.</p>
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Current password</label>
                    <input
                      type="password"
                      value={changePasswordCurrent}
                      onChange={(e) => setChangePasswordCurrent(e.target.value)}
                      className="form-input"
                      placeholder="Current password"
                      autoComplete="current-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New password</label>
                    <input
                      type="password"
                      value={changePasswordNew}
                      onChange={(e) => setChangePasswordNew(e.target.value)}
                      className="form-input"
                      placeholder="New password (min 6 characters)"
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm new password</label>
                    <input
                      type="password"
                      value={changePasswordConfirm}
                      onChange={(e) => setChangePasswordConfirm(e.target.value)}
                      className="form-input"
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!changePasswordCurrent.trim() || !changePasswordNew.trim() || !changePasswordConfirm.trim()) {
                        toast.error('Please fill in all password fields');
                        return;
                      }
                      if (changePasswordNew.length < 6) {
                        toast.error('New password must be at least 6 characters');
                        return;
                      }
                      if (changePasswordNew !== changePasswordConfirm) {
                        toast.error('New password and confirmation do not match');
                        return;
                      }
                      setChangePasswordSaving(true);
                      try {
                        await api.put('/auth/me/password', {
                          currentPassword: changePasswordCurrent,
                          newPassword: changePasswordNew
                        });
                        toast.success('Password changed successfully. Use the new password to log in next time.');
                        setChangePasswordCurrent('');
                        setChangePasswordNew('');
                        setChangePasswordConfirm('');
                      } catch (err) {
                        const msg = err.response?.data?.error || err.message || 'Failed to change password';
                        toast.error(msg);
                      } finally {
                        setChangePasswordSaving(false);
                      }
                    }}
                    disabled={changePasswordSaving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {changePasswordSaving ? 'Changing...' : 'Change password'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return <GeneralSettings />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
        <p className="text-gray-600 dark:text-gray-300">Configure your automation preferences and system settings</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-xl ${
                  activeTab === tab.id
                    ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-300'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                }`}
              >
                <tab.icon className="mr-3 h-5 w-5" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="card">
            <div className="card-body">
              {renderTabContent()}
              
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-end">
                  <button
                    onClick={() => handleSave(activeTab)}
                    className="btn btn-primary"
                  >
                    Save {tabs.find(t => t.id === activeTab)?.name} Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
